import React, { FC, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import {
  CreateProductDiscountPayload,
  ProductDiscount,
  ProductModel,
  UpdateProductDiscountPayload,
} from '../../../../../shared/orders';
import { timeAdjust } from '../../../../../shared/util/time';
import {
  ActionButtonModal,
  DateTime,
  ElementHeader,
  ElementSelector,
  MaterialIcon,
} from '../../../../components';
import { Currency } from '../../../../components/Currency';
import {
  migrateTimezone,
  TimeZoneInputField,
  trimTime,
} from '../../../../components/TimeZoneInputField';
import { Role } from '../../../../models';
import { useTranslation } from '../../../../translations';
import { useConfig, useConvention, useForm, useInputState, useUserSafe } from '../../../../utils';
import { LocalSearchProvider } from '../utils';

interface DiscountInfoProps {
  readonly product: ProductModel;
  readonly onUpdate: () => void;
}

interface DiscountManageProps extends DiscountInfoProps {
  readonly discount?: ProductDiscount;
  readonly setMode: (mode: DiscountInfoMode) => void;
}

const enum DiscountInfoMode {
  View,
  Edit,
}

export const DiscountInfo: FC<DiscountInfoProps> = ({ product, onUpdate }) => {
  const { ts } = useTranslation();
  const [mode, setMode] = useState(DiscountInfoMode.View);
  const [activeDiscount, setActiveDiscount] = useState<ProductDiscount | undefined>(undefined);

  const startEditing = (dis: ProductDiscount) => {
    setActiveDiscount(dis);
    setMode(DiscountInfoMode.Edit);
  };

  if (mode === DiscountInfoMode.View) {
    return (
      <Card className="mt-10 discountList">
        <CardHeader>
          <div className="card-sub-header">{ts('common.discounts')}</div>
          {!product.deletedAt && (
            <div className="float-end">
              <Button
                color="success"
                id="createDiscountBtn"
                onClick={() => {
                  setActiveDiscount(undefined);
                  setMode(DiscountInfoMode.Edit);
                }}
                title={ts('common.add')}
              >
                <MaterialIcon name="add" />
              </Button>
            </div>
          )}
        </CardHeader>
        <CardBody>
          {product.discounts.length === 0 && <i>{ts('there_are_no_discounts_for_this_product')}</i>}
          {product.discounts.map((discount) => (
            <ElementHeader
              icon={{ iconName: 'card_giftcard' }}
              key={discount.id}
              rightContent={
                <DiscountActions
                  discount={discount}
                  onUpdate={onUpdate}
                  productId={product.id}
                  startEditing={startEditing}
                />
              }
              title={<DiscountTitle discount={discount} key={discount.id} />}
            >
              <DiscountBadges discount={discount} />
              &nbsp;
              {discount.roleId && (
                // TODO: i18n
                <Badge className={`discount-role-pill-${discount.roleId}`} color="warning" pill>
                  {ts('restricted_to_role_id')}
                  {discount.roleId}
                </Badge>
              )}
            </ElementHeader>
          ))}
        </CardBody>
      </Card>
    );
  }

  return (
    <DiscountForm
      discount={activeDiscount}
      onUpdate={onUpdate}
      product={product}
      setMode={setMode}
    />
  );
};

export const DiscountForm: FC<DiscountManageProps> = ({ product, onUpdate, discount, setMode }) => {
  const { ts } = useTranslation();
  const { timeZone } = useConvention();

  const { currencySymbol } = useConfig();
  const searchProvider = useMemo(
    () => new LocalSearchProvider(async () => await api.getRoles()),
    [],
  );

  const user = useUserSafe();

  const dateNow = timeAdjust(-60);
  const defaultDate =
    dateNow.getTime() > product.availableFrom!.getTime()
      ? migrateTimezone(trimTime(dateNow), timeZone)
      : product.availableFrom!;

  const isEdit = discount !== undefined;

  const [name, setName] = useInputState(isEdit ? (discount.name ?? '') : '');

  const [amount, setAmount] = useState(isEdit ? discount.price / 100 : product.price / 100);

  const [selectedIds, setSelectedIds] = useState<number[]>(
    isEdit && discount.roleId ? [discount.roleId] : [],
  );

  const [availableTo, setAvailableTo] = useState(isEdit ? discount.availableTo : defaultDate);
  const [availableFrom, setAvailableFrom] = useState(isEdit ? discount.availableFrom : defaultDate);

  const canLimitByRole = user.permissions.includes('system:permission:read');

  const submitForm = useForm(async () => {
    const cleanName = name === '' ? null : name;

    if (isEdit) {
      const payload: UpdateProductDiscountPayload = {
        availableFrom,
        availableTo,
        name: cleanName,
      };

      await api.updateProductDiscount(product.id, discount.id, payload);
    } else {
      const payload: CreateProductDiscountPayload = {
        availableFrom,
        availableTo,
        name: cleanName,
        price: amount,
        roleIds: selectedIds,
      };

      await api.createProductDiscount(product.id, payload);
    }

    onUpdate();
    setMode(DiscountInfoMode.View);
  }, [
    name,
    isEdit,
    onUpdate,
    setMode,
    availableFrom,
    availableTo,
    product.id,
    discount?.id,
    amount,
    selectedIds,
  ]);

  return (
    <Card className="mt-10 createDiscount">
      <CardHeader>
        <div className="card-sub-header">{ts('common.discounts')}</div>
        <div className="float-end">
          <Button
            color="danger"
            id="cancel"
            onClick={() => {
              setMode(DiscountInfoMode.View);
            }}
            title={ts('common.cancel')}
          >
            <MaterialIcon name="cancel" />
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        <Form id="discountForm" onSubmit={submitForm.onSubmit}>
          <Row className="text-center">
            <Col lg={3} xs={5}>
              <strong>{ts('discounted_price')}</strong>
              <h2 style={{ marginTop: '5px' }}>
                <Row>
                  <Col xs={2}>{currencySymbol}</Col>
                  <Col xs={10}>
                    <Input
                      defaultValue={amount}
                      id="price"
                      min={0}
                      name="price"
                      onChange={(e) => {
                        setAmount(Number.parseFloat(e.target.value));
                      }}
                      readOnly={isEdit}
                      required
                      step={0.01}
                      style={{ fontSize: '1.25rem', marginTop: '-3px' }}
                      type="number"
                    />
                  </Col>
                </Row>
              </h2>
            </Col>
            <Col lg={1} xs={2}>
              <MaterialIcon large name="arrow_back_ios" />
            </Col>
            <Col lg={2} xs={5}>
              <strong>{ts('current_price')}</strong>
              <h2 style={{ marginTop: '5px' }}>
                <Currency value={product.price} />
              </h2>
            </Col>
            <Col lg={5} xs={12}>
              <FormGroup style={{ textAlign: 'initial' }}>
                <Label for="roleSelector">
                  {ts('discount.limit_to_role')} <small>{ts('common.optional')}</small>
                </Label>
                <ElementSelector<Role>
                  disabled={!canLimitByRole || isEdit}
                  id="roleSelector"
                  searchProvider={searchProvider}
                  selectionIdsChanged={setSelectedIds}
                />
                {!canLimitByRole && (
                  <FormFeedback invalid>{ts('you_do_not_have_sufficient')}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col lg={6} xs={12}>
              <FormGroup>
                <Label for="availableFrom">{ts('common.available_from')}</Label>
                <TimeZoneInputField
                  defaultValue={isEdit ? discount.availableFrom : defaultDate}
                  id="availableFrom"
                  onChange={setAvailableFrom}
                  required
                  timeZone={timeZone}
                />
              </FormGroup>
            </Col>
            <Col lg={6} xs={12}>
              <FormGroup>
                <Label for="availableTo">{ts('common.available_until')}</Label>
                <TimeZoneInputField
                  defaultValue={isEdit ? discount.availableTo : defaultDate}
                  id="availableTo"
                  min={defaultDate}
                  onChange={setAvailableTo}
                  required
                  timeZone={timeZone}
                />
              </FormGroup>
            </Col>
            <Col lg={12} xs={12}>
              <FormGroup>
                <Label for="name">{ts('name')}</Label>
                <Input name="name" onChange={setName} type="text" value={name} />
              </FormGroup>
            </Col>
            <Col xs={12}>
              <Button
                block
                color="primary"
                id="submitDiscountPrice"
                outline
                style={{ marginTop: '10px' }}
              >
                {ts(isEdit ? 'discount.save' : 'discount.create')}
              </Button>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );
};

const DiscountBadges: FC<{ readonly discount: ProductDiscount }> = ({ discount }) => {
  const { timeZone } = useConvention();
  const { ts, t } = useTranslation();

  const now = Date.now();
  if (discount.deletedAt) {
    return (
      <Badge className="discount-pill-deleted" color="danger" pill>
        {ts('deleted')}
      </Badge>
    );
  }

  if (discount.availableTo.getTime() <= now) {
    return (
      <Badge className="discount-pill-expired" color="danger" pill>
        {ts('vouchers.expired')}
      </Badge>
    );
  }

  if (discount.availableFrom.getTime() > now) {
    return (
      <>
        <UncontrolledTooltip placement="bottom" target={`notYetActive${discount.id}`}>
          {t(
            'discount_available_from',
            <DateTime timeZone={timeZone} value={discount.availableTo} />,
          )}
        </UncontrolledTooltip>
        <Badge
          className="discount-pill-expired"
          color="info"
          id={`notYetActive${discount.id}`}
          pill
        >
          {ts('not_yet_active')}
        </Badge>
      </>
    );
  }

  return (
    <Badge className="discount-pill-active" color="success" pill>
      {ts('active')}
    </Badge>
  );
};

const DiscountActions: FC<{
  readonly discount: ProductDiscount;
  readonly productId: number;
  readonly onUpdate: () => void;
  readonly startEditing: (discount: ProductDiscount) => void;
}> = ({ onUpdate, productId, discount, startEditing }) => {
  const { ts } = useTranslation();

  if (discount.deletedAt) {
    return null;
  }

  return (
    <>
      <Button
        className={`action-edit edit-discount-${discount.id}`}
        color="secondary"
        onClick={() => {
          startEditing(discount);
        }}
        outline
        title={ts('common.edit')}
      >
        <MaterialIcon name="edit" />
      </Button>
      <ActionButtonModal
        actionContent="common.delete"
        buttonContent={<MaterialIcon name="delete" />}
        className={`action-delete delete-discount-${discount.id}`}
        color="danger"
        id={`deleteVoucher${discount.id}`}
        onComplete={async () => {
          await api.deleteProductDiscount(productId, discount.id);
          onUpdate();
        }}
        outline
        title="delete_discount"
      >
        {ts('confirm_delete_discount')}
      </ActionButtonModal>
    </>
  );
};

const DiscountTitle: FC<{ readonly discount: ProductDiscount }> = ({ discount }) => {
  const { t } = useTranslation();
  const { timeZone } = useConvention();

  const detailedText = t(
    'discount_detailed_title',
    <Currency key={1} value={discount.price} />,
    <DateTime key={2} timeZone={timeZone} value={discount.availableFrom} />,
    <DateTime key={3} timeZone={timeZone} value={discount.availableTo} />,
  );

  if (discount.name) {
    return (
      <>
        {discount.name}
        <br />
        <small>{detailedText}</small>
      </>
    );
  }

  return detailedText;
};
